"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

import { USAGE_STATE_PARAMS } from "@/lib/cluckup/usage-event";
import { HQ_USAGE_STATE_PARAMS } from "@/lib/hq/usage-event";

import { randomId, resolveUsageSession } from "./usage-session";

const ENDPOINT = "/cu/usage";
/** Logged-out routes. The layout mounts the beacon above the auth gate, so
 *  without this every welcome/login view fires a request that costs three DB
 *  round trips before 401ing. */
const UNAUTHED = ["/cu/welcome", "/cu/login", "/cu/pin"];
const isUnauthed = (p: string, prefixes: readonly string[]) =>
  prefixes.some((u) => p === u || p.startsWith(`${u}/`));
/** At most one resume event per 5 minutes per session. */
const RESUME_DEBOUNCE_MS = 5 * 60 * 1000;

type NavKind = "load" | "route" | "state" | "resume";

function send(endpoint: string, payload: Record<string, unknown>): void {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  void fetch(endpoint, { method: "POST", body, keepalive: true }).catch(() => {});
}

/** Only allowlisted UI-state params; everything else is dropped so a query
 *  change like ?outlet= does not masquerade as a new view. */
function stateKey(params: URLSearchParams, keys: readonly string[]): string {
  return keys.map((k) => `${k}=${params.get(k) ?? ""}`).join("&");
}

export function UsageBeacon({
  enabled,
  endpoint = ENDPOINT,
  unauthedPrefixes = UNAUTHED,
  sessionKeyPrefix = "cu",
  stateParams: stateParamsProp,
}: {
  enabled: boolean;
  endpoint?: string;
  unauthedPrefixes?: readonly string[];
  sessionKeyPrefix?: string;
  /** Allowlisted UI-state params for THIS app. Without it a non-cu/hq prefix
   *  silently inherits CU's list and every real state flip is dropped. */
  stateParams?: readonly string[];
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastKey = useRef<string | null>(null);
  const lastResume = useRef(0);
  const stateParams =
    stateParamsProp ?? (sessionKeyPrefix === "hq" ? HQ_USAGE_STATE_PARAMS : USAGE_STATE_PARAMS);

  const query = searchParams.toString();

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (isUnauthed(pathname, unauthedPrefixes)) return;

    const params = new URLSearchParams(query);
    const key = `${pathname}?${stateKey(params, stateParams)}`;
    const prevKey = lastKey.current;
    if (prevKey === key) return;
    lastKey.current = key;

    // Read prevKey BEFORE overwriting it — comparing the new key against itself
    // would classify every navigation as "state".
    const navKind: NavKind =
      prevKey === null
        ? "load"
        : prevKey.split("?")[0] === pathname
          ? "state"
          : "route";

    const base = {
      eventId: randomId(),
      sessionId: resolveUsageSession(Date.now(), sessionKeyPrefix),
      path: pathname,
      query,
      navKind,
    };

    if (navKind !== "load") {
      send(endpoint, { ...base, ttfbMs: null, lcpMs: null });
      return;
    }

    // Hard load: capture TTFB now but send NOTHING yet — the event is emitted
    // once, at flush(), so the FINAL LCP rides along. An LCP read at mount is
    // premature. Trade-off: business_date is stamped on ARRIVAL, so a 23:55 load
    // flushed after midnight lands on the next business date.
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    const ttfbMs = nav ? Math.round(nav.responseStart) : null;

    let lcpMs: number | null = null;
    let observer: PerformanceObserver | null = null;
    try {
      observer = new PerformanceObserver((list) => {
        const last = list.getEntries().at(-1);
        if (last) lcpMs = Math.round(last.startTime);
      });
      observer.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      observer = null;
    }

    let sent = false;
    const flush = () => {
      if (sent) return;
      sent = true;
      observer?.disconnect();
      send(endpoint, { ...base, ttfbMs, lcpMs });
    };

    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);

    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [enabled, endpoint, pathname, query, sessionKeyPrefix, stateParams, unauthedPrefixes]);

  // PWA resume — the single most important adoption signal. A shared tablet can
  // sit on /cu/today for days; without this, picking it up records nothing.
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const onResume = () => {
      if (document.visibilityState !== "visible") return;
      if (isUnauthed(window.location.pathname, unauthedPrefixes)) return;
      const now = Date.now();
      if (now - lastResume.current < RESUME_DEBOUNCE_MS) return;
      lastResume.current = now;
      send(endpoint, {
        eventId: randomId(),
        sessionId: resolveUsageSession(now, sessionKeyPrefix),
        path: window.location.pathname,
        query: window.location.search.replace(/^\?/, ""),
        navKind: "resume",
        ttfbMs: null,
        lcpMs: null,
      });
    };

    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("pageshow", onResume);
    return () => {
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("pageshow", onResume);
    };
  }, [enabled, endpoint, sessionKeyPrefix, unauthedPrefixes]);

  return null;
}
